import { isKawadernaLang, type KawadernaLang } from '~/composables/useKawadernaI18n'

const ENGLISH_LEGAL_PATHS = new Set([
  '/en/privacy',
  '/en/terms',
  '/en/delete-account',
])

export function queryLangValue(queryLang: unknown): string | null {
  if (typeof queryLang === 'string' && queryLang) return queryLang
  if (Array.isArray(queryLang) && typeof queryLang[0] === 'string') return queryLang[0]
  return null
}

export function resolvePublicLegalLang(path: string, queryLang?: unknown): KawadernaLang | null {
  const clean = path.replace(/\/+$/, '') || '/'
  if (ENGLISH_LEGAL_PATHS.has(clean)) return 'en'
  const raw = queryLangValue(queryLang)
  return isKawadernaLang(raw) ? raw : null
}

export function usePublicLegalLangLock() {
  return useState<KawadernaLang | null>('public-legal-lang-lock', () => null)
}

export function publicLegalPath(
  slug: 'privacy' | 'terms' | 'delete-account',
  locale?: KawadernaLang | null,
) {
  return locale === 'en' ? `/en/${slug}` : `/${slug}`
}

/** Read ?lang= / /en/* from the current request. Safe in plugins (no useRoute). */
export function readPublicLegalLangFromLocation(): KawadernaLang | null {
  if (import.meta.server) {
    const url = useRequestURL()
    return resolvePublicLegalLang(url.pathname, url.searchParams.get('lang'))
  }
  if (import.meta.client) {
    const url = new URL(window.location.href)
    return resolvePublicLegalLang(url.pathname, url.searchParams.get('lang'))
  }
  return null
}

export function syncPublicLegalLangLock(force?: KawadernaLang | null): KawadernaLang | null {
  const next = force ?? readPublicLegalLangFromLocation()
  const lock = usePublicLegalLangLock()
  if (next) lock.value = next
  return next
}

export async function applyPublicLegalLocale(force?: KawadernaLang | null) {
  const route = useRoute()
  const next = force ?? resolvePublicLegalLang(route.path, route.query.lang)
  const lock = usePublicLegalLangLock()
  lock.value = next
  if (next) {
    const { setLanguage } = useKawadernaI18n()
    await setLanguage(next)
  }
  return next
}
